import {
  IAudio,
  IImage, IVideo,
} from '@/shared/components/types.ts';
import { inject, ref } from 'vue';
import { mediaLoaderInjectionKey } from '@/core/injection-keys.ts';
import { nanoid } from 'nanoid';
import {
  Filesystem,
  Directory,
  FileInfo,
} from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import {
  getLocalETag,
  getRemoteETag,
  saveETag,
} from '@/shared/tools/etag.ts';
import * as SentryVue from '@sentry/vue';
import { FileTransfer } from '@capacitor/file-transfer';
import { createSemaphore } from '@/shared/utils/semaphore';

export enum FileFormat {
  jpg = 'jpg',
  webp = 'webp',
  png = 'png',
}

interface DownloadFallbackOptions {
  /**
   * Timeout in milliseconds to wait for the fallback function to resolve
   * before proceeding with the original download.
   */
  fallbackTimeout?: number;
  /**
   * Function to be called if the download fails or times out.
   * It should return a Promise that resolves to a string URL.
   */
  fallbackFunction: () => Promise<string>;
}

interface BatchDownloadFallbackOptions {
  /**
   * Timeout in milliseconds to wait for the fallback function to resolve
   * before proceeding with the original download.
   */
  fallbackTimeout?: number;
  /**
   * Array of functions to be called if the download fails or times out.
   * Each function should return a Promise that resolves to a string URL.
   */
  fallbackFunctions: DownloadFallbackOptions['fallbackFunction'][];
}

export interface UseMediaLoader {
  /**
   * Load a media file (image, video, or audio) and return a local URL.
   * @param media - The media object containing the URL to load.
   * @param callback - The function to call with the local URL once loaded.
   * @param options - Optional fallback options for handling download failures.
   */
  load: (media: IImage | IVideo | IAudio, callback: (url: string) => void, options?: DownloadFallbackOptions) => string;

  /**
   * Load a batch of media files and return local URLs for each.
   * @param files - An array of media objects to load.
   * @param onLoad - Optional function to call when all files are loaded.
   * @param options - Optional fallback options for handling download failures.
   */
  loadBatch: (files: (IImage | IVideo | IAudio
  )[], onLoad?: () => void, options?: BatchDownloadFallbackOptions) => string;

  /**
   * Cancel a batch of media loads.
   * @param batchId
   */
  cancelLoad: (loadId: string) => void;

  /**
   * Prefetch a media URL into the cache (warming the cache without rendering).
   * Only downloads if not already cached or in progress.
   * @param url - The HTTP(S) URL to prefetch.
   */
  prefetch: (url: string) => void;

  /**
   * Revoke a previously loaded media URL to free up memory.
   * @param url - The URL to revoke.
   */
  revoke: (url: string) => void;
}

enum LoadStrategy {
  Async = 'async',
  Queue = 'queue',
}

/**
 * Represents a media download that is currently in progress.
 * Stores an array of callback functions to be called with the loaded media URL
 * once the download completes or a fallback is used.
 * This allows multiple consumers to be notified when the media is ready,
 * preventing duplicate downloads for the same resource.
 */
interface DownloadInProgress {
  abortController: AbortController;
  requests: {
    id: string;
    callback?: ((url: string) => unknown);
  }[];
}

interface QueueItem {
  id: string;
  /**
   * The URL of the media file to load.
   */
  url: string;

  /**
   * The function to call with the local URL once loaded.
   */
  onDownload?: (url: string) => void;

  /**
   * Optional fallback options for handling download failures.
   */
  fallbackOptions?: DownloadFallbackOptions;
}

let loadStrategy: LoadStrategy;

export const injectMediaLoader = (): UseMediaLoader => {
  const mediaLoader = inject(mediaLoaderInjectionKey);

  if (!mediaLoader) {
    throw new Error('Media loader not found. Make sure to provide it in the root component.');
  }

  return mediaLoader;
};

// TODO: use abort controller to cancel loading media on from unmounted components

export const useMediaLoader = (): UseMediaLoader => {
  loadStrategy = LoadStrategy.Async;

  const downloadedMediaMap = new Map<string, string>();

  const downloadsInProgress = new Map<string, DownloadInProgress>();

  const queue = ref<QueueItem[]>([]);

  const isProcessing = ref(false);

  const semaphore = createSemaphore(4);

  const revalidateInBackground = (url: string, fileName: string) => {
    // Fire-and-forget: do not await, runs in background
    (async () => {
      try {
        const remoteETag = await getRemoteETag(url);

        if (!remoteETag) {
          return; // Server doesn't support ETags
        }

        const localETag = await getLocalETag(fileName);

        if (localETag === remoteETag) {
          return; // Still fresh
        }

        // ETag mismatch -- re-download in background
        const filePath = (await Filesystem.getUri({
          directory: Directory.Data,
          path: fileName,
        })).uri;

        // Delete stale file
        try {
          await Filesystem.deleteFile({
            path: fileName,
            directory: Directory.Data,
          });
        } catch { /* ignore */ }

        const response = await FileTransfer.downloadFile({
          url,
          path: filePath,
        });

        if (response.path) {
          await saveETag(fileName, remoteETag);

          // Update cache so next access gets fresh version
          const newLocalUrl = Capacitor.convertFileSrc(response.path);
          downloadedMediaMap.set(url, newLocalUrl);
        }
      } catch (e) {
        console.warn('Background revalidation failed for', url, e);
      }
    })();
  };

  const downloadMediaToStorage = async (url: string): Promise<string> => {
    console.log('Downloading media to storage:', url);

    // Extract extension from URL, default to .media if none
    const match = url.match(/\.(\w+)(?:\?|$)/);
    const ext = match ? match[1] : 'media';

    // Generate safe filename
    const fileName = btoa(url).replace(/[/+=]/g, '') + '.' + ext;

    // 1. Try to return cached file immediately (cache-first strategy)
    try {
      const info: FileInfo = await Filesystem.stat({
        path: fileName,
        directory: Directory.Data,
      });

      // File exists -- return immediately, revalidate in background
      const localUri = Capacitor.convertFileSrc(info.uri);

      console.log(`Reusing downloaded media from storage:`, url, '→', info.uri);

      revalidateInBackground(url, fileName);

      return localUri;
    } catch {
      // File doesn't exist -- proceed to download
    }

    // 2. Download the file
    const filePath = (await Filesystem.getUri({
      directory: Directory.Data,
      path: fileName,
    })).uri;

    console.log('Downloading media to storage:', filePath);

    const response = await FileTransfer.downloadFile({
      url,
      path: filePath,
    });

    if (!response.path) {
      throw new Error(`Unable to download file ${url}, response: ${JSON.stringify(response, null, 2)}`);
    }

    // 3. Save ETag for future revalidation
    try {
      const remoteETag = await getRemoteETag(url);
      if (remoteETag) {
        await saveETag(fileName, remoteETag);
      }
    } catch {
      // ETag save failure is non-critical
    }

    return Capacitor.convertFileSrc(response.path);
  };

  /**
   * Load a media blob from a URL and return a local URL.
   * @param url
   */
  const downloadMediaBlob = async (url: string): Promise<string> => {
    const download = downloadsInProgress.get(url)!;

    const mediaResponse = await fetch(url, {
      signal: download.abortController.signal,
    });

    // Extract the MIME type from the response headers
    const mimeType = mediaResponse.headers.get('Content-Type') || '';

    if (!mimeType) {
      throw Error('Unable to load media. Could get media mimetype');
    }

    // Convert the response to an ArrayBuffer
    const arrayBuffer = await mediaResponse.arrayBuffer();

    // Create a Blob from the ArrayBuffer and specify the MIME type
    const blob = new Blob([arrayBuffer], {
      type: mimeType,
    });

    return URL.createObjectURL(blob);
  };

  const downloadMedia = async (url: string): Promise<string> => {
    if (!url.startsWith('http')) {
      return url;
    }

    switch (Capacitor.getPlatform()) {
      case 'ios':
        return downloadMediaToStorage(url);
      case 'android':
      case 'web':
      default:
        return downloadMediaBlob(url);
    }
  };

  const registerFallback = (url: string, fallbackOptions: DownloadFallbackOptions): void => {
    const { fallbackTimeout, fallbackFunction } = fallbackOptions;

    setTimeout(async () => {
      const download = downloadsInProgress.get(url);

      if (download) {
        const fallbackResult = await fallbackFunction();

        for (const request of download.requests) {
          request.callback?.(fallbackResult);
        }

        downloadsInProgress.delete(url);
        downloadedMediaMap.set(url, fallbackResult);
      }
    }, fallbackTimeout || 5000);
  };

  /**
   * Process the queue of media downloads.
   * This function will be called when a new item is added to the queue.
   * It will process each item in the queue one by one.
   */
  const processQueue = async () => {
    isProcessing.value = true;

    while (queue.value.length > 0) {
      const queueItem = queue.value.shift();

      if (!queueItem) {
        return;
      }

      const localUrl = downloadedMediaMap.get(queueItem.url);

      if (localUrl) {
        console.log('MEDIA LOADER: URL already loaded. USE LOCAL URL');

        queueItem.onDownload?.(localUrl);

        continue;
      }

      try {
        // ------------------ Check if file with such url is already in progress -------------------
        const download = downloadsInProgress.get(queueItem.url);

        if (download) {
          download.requests.push({
            id: queueItem.id,
            callback: queueItem.onDownload,
          });

          if (queueItem.fallbackOptions) {
            registerFallback(queueItem.url, queueItem.fallbackOptions);
          }

          continue;
        } else {
          downloadsInProgress.set(queueItem.url, {
            abortController: new AbortController(),
            requests: [{
              id: queueItem.id,
              callback: queueItem.onDownload,
            }],
          });
        }
        // ------------------ End Check if download is in progress -------------------

        // ------------------- Create fallback timeout -------------------
        if (queueItem.fallbackOptions) {
          registerFallback(queueItem.url, queueItem.fallbackOptions);
        }
        // ------------------- End Create fallback timeout -------------------

        // ------------------- Load media blob -------------------
        if (loadStrategy === LoadStrategy.Async) {
          (async () => {
            await semaphore.acquire();
            try {
              const result = await downloadMedia(queueItem.url);

              const download = downloadsInProgress.get(queueItem.url);

              if (download) {
                for (const request of download.requests) {
                  request.callback?.(result);
                }

                downloadsInProgress.delete(queueItem.url);
                downloadedMediaMap.set(queueItem.url, result);
              } else {
                // Download was cancelled while in flight — revoke the orphaned blob URL
                URL.revokeObjectURL(result);
              }
            } catch (e) {
              console.error(e);

              SentryVue.captureException(e, {
                extra: {
                  url: queueItem.url,
                },
              });
            } finally {
              semaphore.release();
            }
          })();
        } else {
          const result = await downloadMedia(queueItem.url);

          const download = downloadsInProgress.get(queueItem.url);

          if (download) {
            for (const request of download.requests) {
              request.callback?.(result);
            }
          }

          downloadsInProgress.delete(queueItem.url);
          downloadedMediaMap.set(queueItem.url, result);
        }
        // ------------------- End Load media blob -------------------
      } catch (error) {
        console.error('Error processing promise:', error);
      }
    }

    isProcessing.value = false;
  };

  /**
   * Enqueue a media download.
   * This function will add the download item to the queue and start processing it.
   * @param downloadItem
   */
  const enqueue = (downloadItem: QueueItem) => {
    queue.value.push(downloadItem);

    if (!isProcessing.value) {
      processQueue();
    }
  };

  /**
   * Prefetch a media URL into the cache (warming the cache without rendering components).
   * Only downloads if not already cached or in progress.
   * @param url - The HTTP(S) URL to prefetch.
   */
  const prefetch = (url: string) => {
    if (!url || !url.startsWith('http')) {
      return;
    }
    if (downloadedMediaMap.has(url)) {
      return; // already cached
    }
    if (downloadsInProgress.has(url)) {
      return; // already downloading
    }

    enqueue({
      id: nanoid(),
      url,
    });
  };

  /**
   * Load a media file (image, video, or audio) and return a local URL.
   * @param media - The media object containing the URL to load.
   * @param onDownload - The function to call with the local URL once loaded.
   * @param fallbackOptions - Optional fallback options for handling download failures.
   */
  const load = (media: IImage | IVideo | IAudio, onDownload: (url: string) => void, fallbackOptions?: DownloadFallbackOptions) => {
    const loadId = nanoid();

    enqueue({
      id: loadId,
      url: media.url,
      onDownload,
      fallbackOptions,
    });

    return loadId;
  };

  /**
   * Load a batch of media files and return local URLs for each.
   * @param files - An array of media objects to load.
   * @param onDownload - Optional function to call when all files are loaded.
   * @param fallbackOptions - Optional fallback options for handling download failures.
   */
  const loadBatch = (
    files: (IImage | IVideo | IAudio)[],
    onDownload?: () => void,
    fallbackOptions?: BatchDownloadFallbackOptions,
  ) => {
    const loadBatchId = nanoid();

    const finishedLoads = Array(files.length).fill(null);

    for (const file of files) {
      enqueue({
        id: loadBatchId,
        url: file.url,

        onDownload: (url) => {
          finishedLoads[files.indexOf(file)] = url;

          if (finishedLoads.every((url) => url !== null)) {
            onDownload?.();
          }
        },

        fallbackOptions: fallbackOptions && {
          fallbackTimeout: fallbackOptions.fallbackTimeout,
          fallbackFunction: fallbackOptions.fallbackFunctions[files.indexOf(file)],
        },
      });
    }

    return loadBatchId;
  };

  const cancelLoad = (loadId: string) => {
    Array.from(downloadsInProgress.entries()).forEach(([url, download]) => {
      download.requests = download.requests.filter((request) => request.id !== loadId);

      if (download.requests.length === 0) {
        download.abortController.abort();

        downloadsInProgress.delete(url);
      }
    });
  };

  /**
   * Revoke a previously loaded media URL to free up memory.
   * @param url - The URL to revoke.
   */
  const revoke = (url: string) => {
    if (url && ['web', 'android'].includes(Capacitor.getPlatform())) {
      URL.revokeObjectURL(url);

      const key = [...downloadedMediaMap.entries()].find(([_, value]) => value === url)?.[0];

      console.log('revoke', url, key);

      if (!key) {
        console.warn(`Unable to delete entry with url "${url}" from downloadedMediaMap. Unable to find a match for ${url}`);

        return;
      }

      downloadedMediaMap.delete(key);
    }
  };

  return {
    load,
    loadBatch,
    cancelLoad,
    prefetch,
    revoke,
  };
};
